import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";

import { useFetchGetAllSeries } from "@/shared/actions/erp/productofactorserie.actions";
import type { ConceptoCatalogo } from "./usePreciosConceptosVisita";
import CampoFotoEvidencia from "../evidencias/CampoFotoEvidencia";
import type { FotoEvidencia } from "../evidencias/CampoFotoEvidencia";

/**
 * Selector de serie para un concepto ligado a un producto serializado. Trae las
 * series disponibles del producto en la ubicación de la flota. Si el producto no
 * maneja series (lista vacía), no se muestra nada.
 */
function SerieSelectorConcepto({
  ubicacion,
  codigo,
  value,
  usadas,
  onSelect,
  disabled,
}: {
  ubicacion?: string;
  codigo?: string;
  value?: string;
  usadas: string[];
  onSelect: (serie?: string) => void;
  disabled?: boolean;
}) {
  const { data, isLoading } = useFetchGetAllSeries({
    enabled: !!ubicacion && !!codigo,
    params: { ubicacion, producto: codigo, page_size: 1000 } as any,
  });
  const series: string[] = ((data as any)?.data?.data || [])
    .map((s: any) => s?.serie)
    .filter(Boolean);
  const sinSeriesAqui = !isLoading && series.length === 0 && !value;

  // Si no hay series en la ubicacion de la flota, comprobamos si el producto es
  // SERIALIZADO (tiene series en cualquier otra ubicacion). Asi distinguimos
  // "producto no serializado" (no se muestra selector) de "sin stock en tu
  // flota" (se avisa al tecnico en vez de dejar el campo vacio y confuso).
  const { data: dataGlobal } = useFetchGetAllSeries({
    enabled: !!codigo && sinSeriesAqui,
    params: { producto: codigo, page_size: 1 } as any,
  });
  const productoSerializado =
    (((dataGlobal as any)?.data?.data as any[]) || []).length > 0 ||
    Number((dataGlobal as any)?.data?.count || 0) > 0;

  if (sinSeriesAqui) {
    // Producto sin series en ninguna parte = no serializado: no se muestra.
    if (!productoSerializado) return null;
    // Serializado pero sin stock en la flota del tecnico: se avisa claramente.
    return (
      <TextField
        fullWidth
        size="small"
        disabled
        error
        label="Serie a entregar"
        placeholder="Sin stock en tu flota"
        helperText={`No hay unidades de este equipo en tu flota${
          ubicacion ? ` (${ubicacion})` : ""
        }. Solicita el traslado de stock para poder asignar la serie.`}
      />
    );
  }

  const options = value && !series.includes(value) ? [value, ...series] : series;
  return (
    <Autocomplete
      size="small"
      options={options}
      loading={isLoading}
      value={value || null}
      disabled={disabled}
      getOptionDisabled={(o) => usadas.includes(o) && o !== value}
      onChange={(_, v) => onSelect(v || undefined)}
      renderInput={(params) => (
        <TextField {...params} label="Serie a entregar" placeholder="Elige la serie" />
      )}
    />
  );
}

/**
 * "Otros conceptos" de una visita: lista de conceptos, cada uno con su método de
 * pago (EFECTIVO / FACTURA / COMODATO). Si es FACTURA se ingresa el número de
 * meses a diferir. Componente CONTROLADO y reutilizable.
 *
 * ÚNICO selector: los conceptos vienen del catálogo (Catálogos > Administración),
 * y cada concepto ya trae su PRODUCTO de inventario relacionado + su PRECIO. Al
 * elegir un concepto se copian esos datos. El inventario es solo REFERENCIA aquí;
 * el descuento de stock se hace al completar técnico/activación. En activaciones
 * se puede pasar `stockPorProducto` para avisar si no hay stock suficiente.
 *
 * Se persiste en `agendamiento.observaciones.otros_conceptos` y al finalizar la
 * visita se envía a Softnet como rubros (valor total con IVA).
 */

/** Método de pago de un concepto. Es un texto libre porque los métodos son
 *  PARAMETRIZABLES desde Catálogos > Administración (no un conjunto fijo). */
export type MetodoPagoConcepto = string;

export type ConceptoVisita = {
  concepto: string;
  tipo_pago: MetodoPagoConcepto;
  meses_pago?: number | null;
  valor_total?: number;
  valor_sin_iva?: number;
  iva?: number;
  // Producto de inventario relacionado (copiado del catálogo).
  codigo?: string;
  id_producto?: string;
  descripcion_producto?: string;
  // Serie(s) a entregar del producto serializado (elige técnico/activaciones).
  series?: string[];
  // Evidencia fotográfica (sube el técnico): N° de serie + cliente/ubicación.
  foto_serie?: FotoEvidencia | null;
  foto_evidencia?: FotoEvidencia | null;
};

/** Fallback si no se pasa la lista parametrizada (`metodosPago`). */
export const METODOS_PAGO_CONCEPTO: MetodoPagoConcepto[] = [
  "EFECTIVO",
  "FACTURA",
  "COMODATO",
];

/**
 * Devuelve los NOMBRES de conceptos cuyo producto no tiene stock suficiente en la
 * ubicación de la flota (según `stockPorProducto`: id_producto → stock). Se usa
 * para bloquear el guardado. Vacío si no hay validación de stock (sin flota).
 */
export function conceptosSinStock(
  items: ConceptoVisita[] | null | undefined,
  stockPorProducto?: Record<string, number> | null
): string[] {
  const list = Array.isArray(items) ? items : [];
  if (!stockPorProducto) return [];
  const conteo: Record<string, number> = {};
  for (const it of list) {
    if (it.id_producto) conteo[it.id_producto] = (conteo[it.id_producto] || 0) + 1;
  }
  const nombres: string[] = [];
  for (const it of list) {
    if (!it.id_producto) continue;
    const disp = Number(stockPorProducto[it.id_producto] ?? 0);
    const req = conteo[it.id_producto] || 0;
    if (req > disp && it.concepto && !nombres.includes(it.concepto)) {
      nombres.push(it.concepto);
    }
  }
  return nombres;
}

const ACCENT = "#1565c0";

type Props = {
  value?: ConceptoVisita[] | null;
  onChange?: (value: ConceptoVisita[]) => void;
  readOnly?: boolean;
  /** Catálogo de conceptos (para el único selector). Trae producto + precio. */
  conceptos?: ConceptoCatalogo[];
  /** Stock por id_producto en la ubicación (activaciones): avisa si no alcanza. */
  stockPorProducto?: Record<string, number>;
  /** Ubicación de la flota: habilita elegir la SERIE a entregar (serializados). */
  ubicacionFlota?: string;
  /**
   * Modo técnico: concepto/método/precio READ-ONLY (los definió ventas/activaciones),
   * pero el técnico puede ELEGIR la serie a entregar de cada producto (como elige el
   * ONT en activación). Requiere `ubicacionFlota`. onChange emite el array con la
   * serie asignada por concepto.
   */
  serieEditableOnly?: boolean;
  /** Sube una foto de evidencia (solo técnico); si viene, habilita los campos de foto. */
  onUploadFoto?: (file: File) => Promise<FotoEvidencia>;
  /** Métodos de pago disponibles (parametrizables desde Catálogos > Administración). */
  metodosPago?: string[];
  /** Título de la sección (por defecto "Otros conceptos"). */
  title?: string;
};

const money = (n?: number | null) =>
  n == null ? "—" : `$${Number(n).toFixed(2)}`;

/** Cuota mensual si el pago es diferido (FACTURA con meses): total / meses. */
function cuotaMensual(it: ConceptoVisita): number | null {
  const meses = Number(it.meses_pago) || 0;
  if (it.tipo_pago !== "FACTURA" || meses <= 0) return null;
  return (Number(it.valor_total) || 0) / meses;
}

function metodoColor(
  m?: string
): "success" | "info" | "warning" | "default" | "primary" | "secondary" {
  switch (m) {
    case "EFECTIVO":
      return "success";
    case "FACTURA":
      return "info";
    case "COMODATO":
      return "warning";
    case "Fibrapuntos":
      return "primary";
    case "Retenciones":
      return "secondary";
    default:
      return "default";
  }
}

/**
 * Un ítem de "Detalles y resultados": etiqueta arriba, valor abajo. Sustituye a
 * los chips amontonados para que Total/IVA/Diferido/Stock se lean como datos.
 */
function DetalleItem({
  label,
  value,
  strong = false,
  color,
}: {
  label: string;
  value: string;
  strong?: boolean;
  color?: string;
}) {
  return (
    <Grid item xs={6} sm={4}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={strong ? 800 : 600}
        sx={{ color: color || (strong ? ACCENT : "text.primary"), lineHeight: 1.3 }}
      >
        {value}
      </Typography>
    </Grid>
  );
}

export default function OtrosConceptosVisita({
  value,
  onChange,
  readOnly = false,
  conceptos = [],
  stockPorProducto,
  ubicacionFlota,
  serieEditableOnly = false,
  onUploadFoto,
  metodosPago = METODOS_PAGO_CONCEPTO,
  title = "Otros conceptos",
}: Props) {
  const items: ConceptoVisita[] = Array.isArray(value) ? value : [];

  const emit = (next: ConceptoVisita[]) => onChange && onChange(next);

  const addConcepto = () =>
    emit([...items, { concepto: "", tipo_pago: "EFECTIVO", meses_pago: null }]);

  const removeConcepto = (idx: number) =>
    emit(items.filter((_, i) => i !== idx));

  const updateConcepto = (idx: number, patch: Partial<ConceptoVisita>) =>
    emit(
      items.map((it, i) => {
        if (i !== idx) return it;
        const merged = { ...it, ...patch };
        if (merged.tipo_pago !== "FACTURA") merged.meses_pago = null;
        else if (merged.meses_pago == null) merged.meses_pago = 1;
        // Al elegir el concepto, copiar precio del catálogo. El producto se fija
        // SOLO si el concepto liga un único producto (autoselección); si liga
        // varios, el técnico elegirá cuál entregó (no se fija aquí); si no liga,
        // queda sin producto.
        if (patch.concepto) {
          const cat = conceptos.find((c) => c.concepto === patch.concepto);
          if (cat) {
            merged.valor_total = cat.valor_total;
            merged.valor_sin_iva = cat.valor_sin_iva;
            merged.iva = cat.iva;
            const prods = cat.liga_producto ? cat.productos || [] : [];
            if (prods.length === 1) {
              merged.codigo = prods[0].codigo;
              merged.id_producto = prods[0].id_producto;
              merged.descripcion_producto = prods[0].descripcion;
            } else {
              merged.codigo = undefined;
              merged.id_producto = undefined;
              merged.descripcion_producto = undefined;
            }
            merged.series = [];
          }
        }
        return merged;
      })
    );

  // Conteo por producto (para avisar de stock insuficiente en activaciones).
  const conteoPorProducto: Record<string, number> = {};
  for (const it of items) {
    if (it.id_producto) {
      conteoPorProducto[it.id_producto] =
        (conteoPorProducto[it.id_producto] || 0) + 1;
    }
  }
  const seriesUsadas = items.flatMap((it) => it.series || []).filter(Boolean);
  // Info de stock del producto en la ubicación de la flota (referencia). Devuelve
  // el disponible y si lo requerido (conteo del producto) excede ese stock.
  const stockInfo = (
    it: ConceptoVisita
  ): { disponible: number; insuficiente: boolean } | null => {
    if (!stockPorProducto || !it.id_producto) return null;
    const disponible = Number(stockPorProducto[it.id_producto] ?? 0);
    const requerido = conteoPorProducto[it.id_producto] || 0;
    return { disponible, insuficiente: requerido > disponible };
  };

  // ---------------- SERIE-ONLY (técnico) ----------------
  // Concepto/método/precio read-only; el técnico solo elige la serie a entregar.
  if (serieEditableOnly) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Inventory2OutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin conceptos/equipos registrados para esta visita.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {items.map((it, i) => {
              // Resolver el concepto del catálogo para obtener sus productos ligados.
              const cat = conceptos.find((c) => c.concepto === it.concepto);
              const ligaProducto = cat ? cat.liga_producto : !!it.codigo;
              const productosCat =
                cat?.productos && cat.productos.length > 0
                  ? cat.productos
                  : it.codigo
                  ? [{ codigo: it.codigo, id_producto: it.id_producto, descripcion: it.descripcion_producto }]
                  : [];
              return (
                <Paper key={i} variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                  {/* Cabecera: concepto + chips de método/valor/serie */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={800}>
                      {it.concepto || "—"}
                    </Typography>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: ACCENT }}>
                      {money(it.valor_total)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, mb: ligaProducto ? 1.5 : 0 }}>
                    <Chip size="small" label={it.tipo_pago} color={metodoColor(it.tipo_pago)} variant="outlined" />
                    {cuotaMensual(it) != null && (
                      <Chip size="small" color="info" variant="outlined" label={`${it.meses_pago} × ${money(cuotaMensual(it))}/mes`} />
                    )}
                    {it.series && it.series[0] && (
                      <Chip size="small" color="success" label={`Serie: ${it.series[0]}`} />
                    )}
                  </Stack>

                  {!ligaProducto ? (
                    <Typography variant="caption" color="text.secondary">
                      Concepto sin producto de inventario — no requiere serie.
                    </Typography>
                  ) : (
                    <Grid container spacing={1.5}>
                      {/* Selector de PRODUCTO (de los ligados al concepto) */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Producto entregado"
                          value={it.codigo || ""}
                          onChange={(e) => {
                            const p = productosCat.find((x) => x.codigo === e.target.value);
                            updateConcepto(i, {
                              codigo: p?.codigo,
                              id_producto: p?.id_producto,
                              descripcion_producto: p?.descripcion,
                              series: [],
                            });
                          }}
                        >
                          {productosCat.length === 0 && (
                            <MenuItem value="" disabled>
                              Sin productos ligados en el catálogo
                            </MenuItem>
                          )}
                          {productosCat.map((p) => (
                            <MenuItem key={p.codigo} value={p.codigo}>
                              {p.descripcion ? `${p.descripcion} · ${p.codigo}` : p.codigo}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      {/* Selector de SERIE del producto elegido */}
                      <Grid item xs={12} md={6}>
                        {it.codigo && ubicacionFlota ? (
                          <SerieSelectorConcepto
                            ubicacion={ubicacionFlota}
                            codigo={it.codigo}
                            value={it.series?.[0]}
                            usadas={seriesUsadas}
                            onSelect={(serie) => updateConcepto(i, { series: serie ? [serie] : [] })}
                          />
                        ) : it.codigo ? (
                          <TextField fullWidth size="small" disabled label="Serie a entregar" helperText="Sin ubicación de flota" />
                        ) : (
                          <TextField fullWidth size="small" disabled label="Serie a entregar" helperText="Elige primero el producto" />
                        )}
                      </Grid>
                    </Grid>
                  )}
                  {/* Evidencias del equipo entregado — 2 columnas fijas (no se
                      reacomodan al subir la foto) */}
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <CampoFotoEvidencia
                        label="N° de serie del equipo"
                        value={it.foto_serie}
                        onUpload={onUploadFoto}
                        onChange={(f) => updateConcepto(i, { foto_serie: f })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CampoFotoEvidencia
                        label="Cliente / Ubicación con el equipo"
                        value={it.foto_evidencia}
                        onUpload={onUploadFoto}
                        onChange={(f) => updateConcepto(i, { foto_evidencia: f })}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  }

  // ---------------- READ-ONLY ----------------
  if (readOnly) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Inventory2OutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Sin conceptos registrados.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {items.map((it, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                <Stack direction="row" alignItems="center" gap={1.5} sx={{ flexWrap: "wrap" }}>
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 160 }}>
                    {it.concepto || "—"}
                  </Typography>
                  {it.codigo && (
                    <Chip size="small" icon={<WarehouseOutlinedIcon />} label={it.codigo} variant="outlined" />
                  )}
                  {it.series && it.series[0] && (
                    <Chip size="small" label={`Serie: ${it.series[0]}`} variant="outlined" />
                  )}
                  <Typography variant="body2" fontWeight={700} sx={{ color: ACCENT, minWidth: 72 }}>
                    {money(it.valor_total)}
                  </Typography>
                  <Chip size="small" label={it.tipo_pago} color={metodoColor(it.tipo_pago)} variant="outlined" />
                  {it.tipo_pago === "FACTURA" && (
                    <Typography variant="caption" color="text.secondary">
                      {(it.meses_pago ?? 1)} mes(es) diferido · {money(cuotaMensual(it))}/mes
                    </Typography>
                  )}
                </Stack>
                {(it.foto_serie?.url || it.foto_evidencia?.url) && (
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <CampoFotoEvidencia label="N° de serie del equipo" value={it.foto_serie} readOnly />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CampoFotoEvidencia label="Cliente / Ubicación con el equipo" value={it.foto_evidencia} readOnly />
                    </Grid>
                  </Grid>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    );
  }

  // ---------------- EDITOR ----------------
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Inventory2OutlinedIcon fontSize="small" sx={{ color: ACCENT }} />
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        {items.map((it, i) => {
          const stk = stockInfo(it);
          const insuf = !!stk?.insuficiente;
          return (
            <Paper
              key={i}
              variant="outlined"
              sx={{
                p: 1.75,
                borderRadius: 2,
                bgcolor: insuf ? "rgba(237,108,2,0.06)" : "rgba(21,101,192,0.02)",
                borderColor: insuf ? "warning.main" : undefined,
              }}
            >
              {/* Fila 1: Concepto (ancho) + eliminar */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Concepto"
                  value={it.concepto || ""}
                  onChange={(e) => updateConcepto(i, { concepto: e.target.value })}
                >
                  {conceptos.length === 0 && (
                    <MenuItem value="" disabled>
                      Sin conceptos en el catálogo
                    </MenuItem>
                  )}
                  {conceptos.map((c) => (
                    <MenuItem key={c.concepto} value={c.concepto}>
                      {c.concepto}
                      {c.codigo ? ` · ${c.codigo}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
                <Tooltip title="Quitar concepto">
                  <IconButton size="small" color="error" onClick={() => removeConcepto(i)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {/* Fila 2: Método + Meses */}
              <Grid container spacing={1.5}>
                <Grid item xs={7} sm={5}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Método de pago"
                    value={it.tipo_pago || "EFECTIVO"}
                    onChange={(e) =>
                      updateConcepto(i, {
                        tipo_pago: e.target.value as MetodoPagoConcepto,
                      })
                    }
                  >
                    {(it.tipo_pago && !metodosPago.includes(it.tipo_pago)
                      ? [it.tipo_pago, ...metodosPago]
                      : metodosPago
                    ).map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {it.tipo_pago === "FACTURA" && (
                  <Grid item xs={5} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Meses a diferir"
                      inputProps={{ min: 1, step: 1 }}
                      value={it.meses_pago ?? 1}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        updateConcepto(i, {
                          meses_pago: isNaN(n) || n < 1 ? 1 : n,
                        });
                      }}
                    />
                  </Grid>
                )}
              </Grid>

              {it.concepto && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed", borderColor: "divider" }}>
                  {/* ── ARRIBA: equipo + serie a entregar ── */}
                  {it.codigo && (
                    <Box sx={{ mb: 2 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        sx={{ mb: 1 }}
                      >
                        <WarehouseOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", mt: 0.25, flexShrink: 0 }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.2 }}
                          >
                            Equipo de inventario
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ lineHeight: 1.3, wordBreak: "break-word" }}
                          >
                            {it.codigo}
                            {it.descripcion_producto ? ` · ${it.descripcion_producto}` : ""}
                          </Typography>
                        </Box>
                      </Stack>
                      {ubicacionFlota ? (
                        <Box sx={{ maxWidth: 420 }}>
                          <SerieSelectorConcepto
                            ubicacion={ubicacionFlota}
                            codigo={it.codigo}
                            value={it.series?.[0]}
                            usadas={seriesUsadas}
                            onSelect={(serie) =>
                              updateConcepto(i, { series: serie ? [serie] : [] })
                            }
                          />
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Sin ubicación de flota — no se puede elegir la serie.
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* ── AL FINAL: detalles y resultados (como ítems) ── */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: insuf ? "warning.light" : "divider",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      Detalles y resultados
                    </Typography>
                    <Grid container spacing={1.5}>
                      <DetalleItem label="Total" value={money(it.valor_total)} strong />
                      {typeof it.iva === "number" && (
                        <DetalleItem label="IVA" value={money(it.iva)} />
                      )}
                      {cuotaMensual(it) != null && (
                        <DetalleItem
                          label="Diferido"
                          value={`${it.meses_pago} × ${money(cuotaMensual(it))}/mes`}
                        />
                      )}
                      {stk && (
                        <DetalleItem
                          label="Stock en flota"
                          value={
                            stk.insuficiente
                              ? `Insuficiente (disp: ${stk.disponible})`
                              : `${stk.disponible} disponible(s)`
                          }
                          color={stk.insuficiente ? "warning.main" : "success.main"}
                        />
                      )}
                    </Grid>
                  </Box>
                </Box>
              )}
            </Paper>
          );
        })}
      </Stack>

      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        size="small"
        onClick={addConcepto}
        sx={{ mt: 1.5 }}
      >
        Agregar concepto
      </Button>
    </Box>
  );
}
